import { Component } from '@theme/component';
import { storyOf, track } from '@theme/r-analytics';

/**
 * <r-story-tabs> — the boards' vertical tab list and its panel.
 *
 * Drawn on the Natural Standard board (Rocky-Retheme-2026 3495:1902 /
 * 3495:2005) as a column of labels with a rule under each, beside a single
 * panel of copy. Content and arrangement are the block's and the layout's; this
 * element owns only the state.
 *
 * PROGRESSIVE, deliberately. The block renders every panel visible and in flow,
 * and only this element's `data-ready` flag switches the stylesheet to the
 * stacked single-panel presentation. If the module never loads, all three
 * panels are still readable — a tab component that hides two thirds of its copy
 * behind JS that failed is the one failure mode worth engineering out.
 *
 * Panels stack in ONE grid cell rather than being removed, so the band is as
 * tall as its longest panel and switching tabs never moves the page under the
 * reader. `visibility: hidden` (the stylesheet's, not this file's) is what takes
 * the inactive ones out of the accessibility tree and the tab order.
 *
 * Selection follows focus (WAI-ARIA APG "automatic activation") — the panels are
 * static copy, so there is nothing to load and no reason to make the reader
 * press Enter.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement[]} tabs - the tab buttons, in board order
 * @property {HTMLElement[]} panels - the panels, index-aligned with `tabs`
 * @extends {Component<Refs>}
 */
class RStoryTabs extends Component {
  requiredRefs = ['tabs', 'panels'];

  /** Index of the selected tab. @type {number} */
  #active = 0;

  connectedCallback() {
    super.connectedCallback();

    // The stylesheet's single-panel presentation is keyed to this. Set before
    // the first #apply so the panels never flash all-visible after upgrade.
    this.dataset.ready = '';

    const marked = this.refs.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.#apply(marked < 0 ? 0 : marked, false);
  }

  /**
   * Select the tab that was clicked.
   * @param {Event} event
   */
  select = (event) => {
    const tab = /** @type {Element | null} */ (event.target)?.closest('[role="tab"]');
    if (!tab) return;

    const index = this.refs.tabs.indexOf(/** @type {HTMLButtonElement} */ (tab));
    if (index < 0) return;

    this.#trackTab(index);
    this.#apply(index, false);
  };

  /**
   * WS23 — rocky_story_engage for a reader-initiated tab change; the initial selection is not one.
   * @param {number} index
   */
  #trackTab(index) {
    if (index === this.#active) return;
    const tab = this.refs.tabs[index]?.textContent?.trim();
    track('story_engage', { block: 'tab', story_id: storyOf(this).story_id, tab, item: tab });
  }

  /**
   * Roving focus. Vertical list, so Up/Down are the movement keys; Left/Right
   * are honoured too because the layout lays the labels out as a column at every
   * width and a reader may still reach for them.
   * @param {KeyboardEvent} event
   */
  handleKeyDown = (event) => {
    const last = this.refs.tabs.length - 1;
    let next = null;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = this.#active === last ? 0 : this.#active + 1;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        next = this.#active === 0 ? last : this.#active - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    // Only now — an unhandled key must keep its default (Tab out, typeahead).
    event.preventDefault();
    this.#trackTab(next);
    this.#apply(next, true);
  };

  /**
   * @param {number} index
   * @param {boolean} focus - move focus to the newly selected tab
   */
  #apply(index, focus) {
    const { tabs, panels } = this.refs;

    this.#active = index;

    tabs.forEach((tab, i) => {
      const on = i === index;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      // Roving tabindex: one stop for the whole list, arrows move within it.
      tab.tabIndex = on ? 0 : -1;
      tab.toggleAttribute('data-active', on);
    });

    panels.forEach((panel, i) => {
      panel.toggleAttribute('data-active', i === index);
    });

    if (focus) tabs[index]?.focus();
  }
}

if (!customElements.get('r-story-tabs')) {
  customElements.define('r-story-tabs', RStoryTabs);
}
